import { Box, IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import React from 'react';
import {
    CTableWrapper,
    DPageLoader,
    EditIcon,
    OptionalDataIcon,
} from '../../../dum';
import {
    SUpdateOrderSaleDetailsDialog,
    SUpsertExpenseDialog,
    SUpsertOrderSale,
} from '../../../smart';
import {
    formatDate,
    formatDateTime,
    formatFloat,
    sumBy,
} from '../../../../helpers';
import {
    AccountingWorkSheets,
    ExpenseRow,
    formatTransferList,
    LedgerRow,
    OrderSaleRow,
} from './accounting-export-rules';

// The worksheet rows carry raw values so the Excel export can write real
// numeric/date cells; the screen formats them here, at the point of display.
// `null` means "no value on this row" and stays blank instead of reading 0.00.
const money = (value: number | null) =>
    value === null ? '' : formatFloat(value);
const calendarDate = (value: string | null) => (value ? formatDate(value) : '');
const instant = (value: string | null) => (value ? formatDateTime(value) : '');

// A canceled document must not keep a transfer attached; that is not enforced at
// capture time yet, so the leftovers are flagged here instead of being hidden.
const warningMark = (text: string) => (
    <Box
        component={'span'}
        sx={{ color: 'error.main', fontWeight: 700, whiteSpace: 'nowrap' }}
    >
        {`⚠ ${text}`}
    </Box>
);

const pendingMark = (text: string) => (
    <Box
        component={'span'}
        sx={{ color: 'warning.main', fontWeight: 700, whiteSpace: 'nowrap' }}
    >
        {`⚠ ${text}`}
    </Box>
);

const reviewMarks = (missing: string[]) => {
    if (missing.length === 0) return null;
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {pendingMark(missing.join(', '))}
        </Box>
    );
};

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            style={{ height: '100%' }}
            {...other}
        >
            {value === index && <Box sx={{ height: '100%' }}>{children}</Box>}
        </div>
    );
}

export function AccountingTabs({
    worksheets,
    loading,
    onEntityEdited,
}: {
    worksheets: AccountingWorkSheets;
    loading?: boolean;
    onEntityEdited?: () => void;
}) {
    const {
        accountsPages,
        orderSalePage,
        canceledOrderSalePage,
        expensePage,
        canceledExpensePage,
    } = worksheets;
    const [value, setValue] = React.useState(0);

    const [openSale, setOpenSale] = React.useState(false);
    const [openExpense, setOpenExpense] = React.useState(false);
    const [openSaleDetails, setOpenSaleDetails] = React.useState(false);
    const [selectedId, setSelectedId] = React.useState<number | null>(null);

    const openRow = (row: LedgerRow) => {
        if (row.expenseId != null) {
            setSelectedId(row.expenseId);
            setOpenExpense(true);
        }
    };

    const openSaleUpsert = (row: LedgerRow) => {
        if (row.orderSaleId == null) return;
        setSelectedId(row.orderSaleId);
        setOpenSale(true);
    };

    const openSaleDetailsDialog = (row: LedgerRow) => {
        if (row.orderSaleId == null) return;
        setSelectedId(row.orderSaleId);
        setOpenSaleDetails(true);
    };

    const renderSaleActions = (row: LedgerRow) => {
        if (row.orderSaleId == null) {
            return null;
        }
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Tooltip arrow title={'Editar datos opcionales'}>
                    <IconButton
                        size={'small'}
                        onClick={(e) => {
                            e.stopPropagation();
                            openSaleDetailsDialog(row);
                        }}
                    >
                        <OptionalDataIcon fontSize={'small'} />
                    </IconButton>
                </Tooltip>
                <Tooltip arrow title={'Editar venta'}>
                    <IconButton
                        size={'small'}
                        onClick={(e) => {
                            e.stopPropagation();
                            openSaleUpsert(row);
                        }}
                    >
                        <EditIcon fontSize={'small'} />
                    </IconButton>
                </Tooltip>
            </Box>
        );
    };

    const openExpenseRow = (row: ExpenseRow) => {
        setSelectedId(row.id);
        setOpenExpense(true);
    };

    const openOrderSaleRow = (row: OrderSaleRow) => {
        setSelectedId(row.id);
        setOpenSale(true);
    };

    const renderOrderSaleActions = (row: OrderSaleRow) => (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Tooltip arrow title={'Editar datos opcionales'}>
                <IconButton
                    size={'small'}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedId(row.id);
                        setOpenSaleDetails(true);
                    }}
                >
                    <OptionalDataIcon fontSize={'small'} />
                </IconButton>
            </Tooltip>
            <Tooltip arrow title={'Editar venta'}>
                <IconButton
                    size={'small'}
                    onClick={(e) => {
                        e.stopPropagation();
                        openOrderSaleRow(row);
                    }}
                >
                    <EditIcon fontSize={'small'} />
                </IconButton>
            </Tooltip>
        </Box>
    );

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    if (loading) {
        return <DPageLoader />;
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                }}
            >
                {/* One tab per own account plus the four registers overflows a normal
                viewport. MUI's default `standard` variant clips the overflow with no
                way to reach it, which hid the last tab (canceled proveedores). */}
                <Tabs
                    value={value}
                    onChange={handleChange}
                    variant={'scrollable'}
                    scrollButtons={'auto'}
                    allowScrollButtonsMobile
                    aria-label={'Registros de contabilidad'}
                >
                    {accountsPages.map(({ accountName }) => {
                        return <Tab key={accountName} label={accountName} />;
                    })}
                    <Tab label={'Facturas emitidas'} />
                    <Tab label={'Facturas canceladas (clientes)'} />
                    <Tab label={'Facturas de proveedores'} />
                    <Tab label={'Facturas canceladas (proveedores)'} />
                </Tabs>
                <Box
                    sx={{
                        mt: 2,
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                        '& .MuiTableContainer-root': { height: '100%' },
                    }}
                >
                    {accountsPages.map(({ accountName, data }, index) => {
                        return (
                            <CustomTabPanel
                                key={accountName}
                                value={value}
                                index={index}
                            >
                                <Box
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        minHeight: 0,
                                    }}
                                >
                                    <Box sx={{ flex: 1, minHeight: 0 }}>
                                        <CTableWrapper
                                            stickyHeader
                                            items={[
                                                {
                                                    title: '#',
                                                    content: (val) => {
                                                        return val.transferIndex;
                                                    },
                                                    textAlign: 'right',
                                                },
                                                {
                                                    title: 'De',
                                                    content: (val) =>
                                                        val.fromAccountName,
                                                },
                                                {
                                                    title: 'Para',
                                                    content: (val) =>
                                                        val.toAccountName,
                                                },
                                                {
                                                    title: 'Fecha',
                                                    textAlign: 'center',
                                                    content: (val) =>
                                                        instant(
                                                            val.transferDate,
                                                        ),
                                                },
                                                {
                                                    title: 'Folio',
                                                    content: (val) =>
                                                        val.orderCode,
                                                },
                                                {
                                                    title: 'Fecha del recibo',
                                                    textAlign: 'center',
                                                    content: (val) =>
                                                        calendarDate(
                                                            val.receiptIssueDate,
                                                        ),
                                                },
                                                {
                                                    title: 'Cantidad transferida',
                                                    content: (val) =>
                                                        money(
                                                            val.transferredAmount,
                                                        ),
                                                    textAlign: 'right',
                                                },
                                                {
                                                    title: 'Total',
                                                    content: (val) =>
                                                        money(val.receiptTotal),
                                                    textAlign: 'right',
                                                },
                                                {
                                                    title: 'IVA',
                                                    content: (val) =>
                                                        money(val.receiptTax),
                                                    textAlign: 'right',
                                                },
                                                {
                                                    title: 'IVA retenido',
                                                    content: (val) =>
                                                        money(val.taxRetained),
                                                    textAlign: 'right',
                                                },
                                                {
                                                    title: 'ISR retenido',
                                                    content: (val) =>
                                                        money(
                                                            val.nonTaxRetained,
                                                        ),
                                                    textAlign: 'right',
                                                },
                                                {
                                                    title: 'Notas',
                                                    content: (val) => val.notes,
                                                },
                                                {
                                                    title: 'Complemento',
                                                    content: (val) =>
                                                        val.supplement,
                                                },
                                                {
                                                    title: 'Nota de credito',
                                                    content: (val) =>
                                                        val.creditNote,
                                                },
                                                {
                                                    title: 'Alerta',
                                                    content: (val) => {
                                                        if (val.documentCanceled) {
                                                            return warningMark('DOCUMENTO CANCELADO');
                                                        }
                                                        const missing: string[] = [];
                                                        if (val.missingFolio) missing.push('Falta factura');
                                                        if (val.missingSupplement) missing.push('Complemento');
                                                        if (val.missingCreditNote) missing.push('Nota de crédito');
                                                        if (val.missingTax) missing.push('IVA');
                                                        return reviewMarks(missing);
                                                    },
                                                },
                                                {
                                                    title: '',
                                                    textAlign: 'center',
                                                    content: (val) =>
                                                        renderSaleActions(val),
                                                },
                                            ]}
                                            data={data}
                                            onRowDoubleClick={openRow}
                                            totalsRow={[
                                                null,
                                                'Total',
                                                null,
                                                null,
                                                null,
                                                null,
                                                formatFloat(
                                                    sumBy(
                                                        data,
                                                        (r) =>
                                                            r.transferredAmount,
                                                    ),
                                                ),
                                                formatFloat(
                                                    sumBy(
                                                        data,
                                                        (r) => r.receiptTotal,
                                                    ),
                                                ),
                                                formatFloat(
                                                    sumBy(
                                                        data,
                                                        (r) => r.receiptTax,
                                                    ),
                                                ),
                                                formatFloat(
                                                    sumBy(
                                                        data,
                                                        (r) => r.taxRetained,
                                                    ),
                                                ),
                                                formatFloat(
                                                    sumBy(
                                                        data,
                                                        (r) => r.nonTaxRetained,
                                                    ),
                                                ),
                                                null,
                                                null,
                                                null,
                                                null,
                                                null,
                                            ]}
                                        />
                                    </Box>
                                </Box>
                            </CustomTabPanel>
                        );
                    })}

                    {/* Facturas emitidas (non-canceled) */}
                    <CustomTabPanel index={accountsPages.length} value={value}>
                        <CTableWrapper
                            stickyHeader
                            items={[
                                {
                                    title: 'Folio',
                                    content: (val) => val.invoiceCode,
                                },
                                {
                                    title: 'Fecha de emisión',
                                    textAlign: 'center',
                                    content: (val) => calendarDate(val.date),
                                },
                                {
                                    title: 'Cliente',
                                    content: (val) => val.accountName,
                                },
                                {
                                    title: 'Total',
                                    content: (val) => money(val.total),
                                    textAlign: 'right',
                                },
                                {
                                    title: 'IVA',
                                    content: (val) => money(val.tax),
                                    textAlign: 'right',
                                },
                                {
                                    title: 'Cantidad transferida',
                                    content: (val) =>
                                        money(val.transferReceiptsTotal),
                                    textAlign: 'right',
                                },
                                {
                                    title: 'Transferencias',
                                    content: (val) =>
                                        formatTransferList(val.transfers),
                                },
                                {
                                    title: 'Complemento',
                                    content: (val) => val.supplement,
                                },
                                {
                                    title: 'Nota de credito',
                                    content: (val) => val.creditNote,
                                },
                                {
                                    title: 'Notas',
                                    content: (val) => val.notes,
                                },
                                {
                                    title: 'Alerta',
                                    content: (val) => {
                                        const missing: string[] = [];
                                        if (val.missingInvoiceCode) missing.push('Falta factura');
                                        if (val.missingSupplement) missing.push('Complemento');
                                        if (val.missingCreditNote) missing.push('Nota de crédito');
                                        if (val.missingTax) missing.push('IVA');
                                        return reviewMarks(missing);
                                    },
                                },
                                {
                                    title: '',
                                    textAlign: 'center',
                                    content: (val) => renderOrderSaleActions(val),
                                },
                            ]}
                            data={orderSalePage}
                            onRowDoubleClick={openOrderSaleRow}
                            // Totals cover every row on screen, including excluded
                            // ones; the export's own totals live in the workbook.
                            totalsRow={[
                                'Total',
                                null,
                                null,
                                formatFloat(
                                    sumBy(orderSalePage, (r) => r.total),
                                ),
                                formatFloat(sumBy(orderSalePage, (r) => r.tax)),
                                formatFloat(
                                    sumBy(
                                        orderSalePage,
                                        (r) => r.transferReceiptsTotal,
                                    ),
                                ),
                                null,
                                null,
                                null,
                                null,
                                null,
                                null,
                            ]}
                        />
                    </CustomTabPanel>

                    {/* Facturas canceladas (clientes) */}
                    <CustomTabPanel
                        index={accountsPages.length + 1}
                        value={value}
                    >
                        <CTableWrapper
                            stickyHeader
                            items={[
                                {
                                    title: 'Folio',
                                    content: (val) => val.invoiceCode,
                                },
                                {
                                    title: 'Fecha de emisión',
                                    textAlign: 'center',
                                    content: (val) => calendarDate(val.date),
                                },
                                {
                                    title: 'Cliente',
                                    content: (val) => val.accountName,
                                },
                                {
                                    title: 'Total',
                                    content: (val) => money(val.total),
                                    textAlign: 'right',
                                },
                                {
                                    title: 'IVA',
                                    content: (val) => money(val.tax),
                                    textAlign: 'right',
                                },
                                {
                                    title: 'Alerta',
                                    content: (val) =>
                                        val.transfers.length > 0
                                            ? warningMark(
                                                  'TRANSFERENCIA LIGADA',
                                              )
                                            : null,
                                },
                            ]}
                            data={canceledOrderSalePage}
                            totalsRow={[
                                'Total',
                                null,
                                null,
                                formatFloat(
                                    sumBy(
                                        canceledOrderSalePage,
                                        (r) => r.total,
                                    ),
                                ),
                                formatFloat(
                                    sumBy(canceledOrderSalePage, (r) => r.tax),
                                ),
                                null,
                            ]}
                        />
                    </CustomTabPanel>

                    {/* Facturas de proveedores (non-canceled) */}
                    <CustomTabPanel
                        index={accountsPages.length + 2}
                        value={value}
                    >
                        <CTableWrapper
                            stickyHeader
                            items={[
                                {
                                    title: 'Folio',
                                    content: (val) => val.orderCode,
                                },
                                {
                                    title: 'Fecha de emisión',
                                    textAlign: 'center',
                                    content: (val) => calendarDate(val.date),
                                },
                                {
                                    title: 'Cliente',
                                    content: (val) => val.accountName,
                                },
                                {
                                    title: 'IVA',
                                    content: (val) => money(val.tax),
                                    textAlign: 'right',
                                },
                                {
                                    title: 'IVA retenido',
                                    content: (val) => money(val.taxRetained),
                                    textAlign: 'right',
                                },
                                {
                                    title: 'ISR retenido',
                                    content: (val) => money(val.nonTaxRetained),
                                    textAlign: 'right',
                                },
                                {
                                    title: 'Importe de la factura',
                                    content: (val) => money(val.totalWithTax),
                                    textAlign: 'right',
                                },
                                {
                                    title: 'Cantidad transferida',
                                    content: (val) =>
                                        money(val.transferReceiptsTotal),
                                    textAlign: 'right',
                                },
                                {
                                    title: 'Transferencias',
                                    content: (val) =>
                                        formatTransferList(val.transfers),
                                },
                                {
                                    title: 'Complemento',
                                    content: (val) => val.supplement,
                                },
                                {
                                    title: 'Notas',
                                    content: (val) => val.notes,
                                },
                                {
                                    title: 'Alerta',
                                    content: (val) => {
                                        const missing: string[] = [];
                                        if (val.missingExternalCode) missing.push('Falta factura');
                                        if (val.missingSupplement) missing.push('Complemento');
                                        if (val.missingTax) missing.push('IVA');
                                        return reviewMarks(missing);
                                    },
                                },
                            ]}
                            data={expensePage}
                            onRowDoubleClick={openExpenseRow}
                            totalsRow={[
                                'Total',
                                null,
                                null,
                                formatFloat(sumBy(expensePage, (r) => r.tax)),
                                formatFloat(
                                    sumBy(expensePage, (r) => r.taxRetained),
                                ),
                                formatFloat(
                                    sumBy(expensePage, (r) => r.nonTaxRetained),
                                ),
                                formatFloat(
                                    sumBy(expensePage, (r) => r.totalWithTax),
                                ),
                                formatFloat(
                                    sumBy(
                                        expensePage,
                                        (r) => r.transferReceiptsTotal,
                                    ),
                                ),
                                null,
                                null,
                                null,
                                null,
                            ]}
                        />
                    </CustomTabPanel>

                    {/* Facturas canceladas (proveedores) */}
                    <CustomTabPanel
                        index={accountsPages.length + 3}
                        value={value}
                    >
                        <CTableWrapper
                            stickyHeader
                            items={[
                                {
                                    title: 'Folio',
                                    content: (val) => val.orderCode,
                                },
                                {
                                    title: 'Fecha de emisión',
                                    textAlign: 'center',
                                    content: (val) => calendarDate(val.date),
                                },
                                {
                                    title: 'Cliente',
                                    content: (val) => val.accountName,
                                },
                                {
                                    title: 'IVA',
                                    content: (val) => money(val.tax),
                                    textAlign: 'right',
                                },
                                {
                                    title: 'IVA retenido',
                                    content: (val) => money(val.taxRetained),
                                    textAlign: 'right',
                                },
                                {
                                    title: 'ISR retenido',
                                    content: (val) => money(val.nonTaxRetained),
                                    textAlign: 'right',
                                },
                                {
                                    title: 'Importe de la factura',
                                    content: (val) => money(val.totalWithTax),
                                    textAlign: 'right',
                                },
                                {
                                    title: 'Complemento',
                                    content: (val) => val.supplement,
                                },
                                {
                                    title: 'Alerta',
                                    content: (val) =>
                                        val.transfers.length > 0
                                            ? warningMark(
                                                  'TRANSFERENCIA LIGADA',
                                              )
                                            : null,
                                },
                            ]}
                            data={canceledExpensePage}
                            totalsRow={[
                                'Total',
                                null,
                                null,
                                formatFloat(
                                    sumBy(canceledExpensePage, (r) => r.tax),
                                ),
                                formatFloat(
                                    sumBy(
                                        canceledExpensePage,
                                        (r) => r.taxRetained,
                                    ),
                                ),
                                formatFloat(
                                    sumBy(
                                        canceledExpensePage,
                                        (r) => r.nonTaxRetained,
                                    ),
                                ),
                                formatFloat(
                                    sumBy(
                                        canceledExpensePage,
                                        (r) => r.totalWithTax,
                                    ),
                                ),
                                null,
                                null,
                            ]}
                        />
                    </CustomTabPanel>
                </Box>
            </Box>
            <SUpsertOrderSale
                open={openSale}
                setOpen={(o) => {
                    setOpenSale(o);
                    if (!o) onEntityEdited?.();
                }}
                orderSaleId={selectedId}
            />
            <SUpsertExpenseDialog
                open={openExpense}
                setOpen={(o) => {
                    setOpenExpense(o);
                    if (!o) onEntityEdited?.();
                }}
                expenseId={selectedId}
            />
            <SUpdateOrderSaleDetailsDialog
                open={openSaleDetails}
                setOpen={(o) => {
                    setOpenSaleDetails(o);
                    if (!o) onEntityEdited?.();
                }}
                orderSaleId={selectedId}
            />
        </>
    );
}
